import {
  CurrencyType,
  EventAuditTimelineAction,
  UserEventStatus,
} from "../enums";
import { endpointsMetadata } from "../infrastructure/routes/endpointsMetadata";
import { IHistoryConfig } from "../components/history/models/historyModels";

export const EVENT_HISTORY_CUTOFF_DATE = new Date("2026-04-04");

function escapeHtml(str: string): string {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function translateStatus(statusCode: number | undefined): string {
  switch (statusCode) {
    case UserEventStatus.Pending:
      return "Изчакване";
    case UserEventStatus.Confirmed:
      return "Потвърден";
    case UserEventStatus.Arrived:
      return "Пристигнал";
    case UserEventStatus.OnlyPaid:
      return "Само платил";
    case UserEventStatus.WaitingList:
      return "Чакаща листа";
    case UserEventStatus.Missed:
      return "Пропуснал";
    case UserEventStatus.PenaltyCancelled:
      return "Отказал с очаквано плащане";
    default:
      return "Неизвестен";
  }
}

export function generateEventAuditText(
  action: number,
  payload: Record<string, any> | null | undefined,
  actorDisplayName: string | null | undefined,
  _timezone?: string,
  _locale = "bg",
): string {
  const actor = escapeHtml(actorDisplayName ?? "Системата");
  const p = payload ?? {};

  switch (action as EventAuditTimelineAction) {
    case EventAuditTimelineAction.EventCreated:
      if (p.isPartOfBulk) {
        const bulkCreateCount = p.affectedCount
          ? ` (${p.affectedCount} общо)`
          : "";
        return `${actor} създаде събитието като част от поредица${bulkCreateCount}`;
      }
      return `${actor} създаде събитието`;

    case EventAuditTimelineAction.EventEdited:
      return `${actor} редактира събитието`;

    case EventAuditTimelineAction.EventBulkEdited:
      if (p.isPartOfBulk) {
        const bulkEditCount = p.affectedCount
          ? ` (${p.affectedCount} общо)`
          : "";
        return `${actor} редактира събитието от поредица${bulkEditCount}`;
      }
      return p.affectedCount
        ? `${actor} редактира ${p.affectedCount} повтарящи се събития`
        : `${actor} редактира групата повтарящи се събития`;

    case EventAuditTimelineAction.EventDeleted: {
      if (p.isPartOfBulk) {
        const bulkDeleteCount = p.affectedCount
          ? ` (${p.affectedCount} общо)`
          : "";
        return `${actor} изтри събитието чрез масово изтриване${bulkDeleteCount}`;
      }
      return `${actor} изтри събитието`;
    }

    case EventAuditTimelineAction.EventRestored:
      return `${actor} възстанови събитието`;

    case EventAuditTimelineAction.EventHardDeleted:
      return `${actor} изтри окончателно събитието`;

    case EventAuditTimelineAction.EventDuplicated:
      return `${actor} дублира събитието`;

    case EventAuditTimelineAction.PortalAppointmentBooked:
      return `${actor} направи резервация`;

    case EventAuditTimelineAction.InstructorBulkAssigned: {
      const bulkAssignCount = p.affectedCount
        ? ` (${p.affectedCount} общо)`
        : "";
      return `${actor} смени инструктора чрез масово задаване${bulkAssignCount}`;
    }

    case EventAuditTimelineAction.ReservationAdded: {
      if (p.isSelfReservation) {
        return `Нова потребителска резервация: ${p.userName ?? actor}`;
      }
      const count: number = p.userCount ?? 1;
      const label = count === 1 ? "1 потребител" : `${count} потребители`;
      let addedText = `${actor} добави ${label}`;
      if (p.markedAsArrived) {
        const arrivedParts: string[] = [];
        if (p.pricePaid != null) {
          const currencySymbol =
            p.currencyId === CurrencyType.BGN ? "лв." : "€";
          arrivedParts.push(`${p.pricePaid} ${currencySymbol}`);
        }
        if (p.paymentType) arrivedParts.push(p.paymentType);
        addedText += arrivedParts.length
          ? ` — статус: Пристигнал, ${arrivedParts.join(", ")}`
          : " — статус: Пристигнал";
      }
      return addedText;
    }

    case EventAuditTimelineAction.ReservationDeleted:
      return `${actor} премахна резервацията на ${p.userName ?? "потребител"}`;

    case EventAuditTimelineAction.ReservationPermanentlyDeleted: {
      const target = p.userName ?? "потребител";
      const userRef = p.userId ? ` #${p.userId}` : "";
      const resRef = p.reservationId ? ` (резервация #${p.reservationId})` : "";
      if (p.reason) {
        return `Перманентно изтрита резервация${resRef} [${target}${userRef}] поради ${p.reason}`;
      }
      return `${actor} изтри перманентно резервацията на ${target}${userRef}${resRef}`;
    }

    case EventAuditTimelineAction.ReservationRestored:
      return `${actor} възстанови резервацията на ${p.userName ?? "потребител"}`;

    case EventAuditTimelineAction.ReservationStatusChanged: {
      const fromLabel = translateStatus(p.fromStatus);
      const toLabel = translateStatus(p.toStatus);
      let statusText = `${actor} смени статуса на ${p.userName ?? "потребител"} от „${fromLabel}" към „${toLabel}"`;
      if (p.toStatus === UserEventStatus.Arrived) {
        const parts: string[] = [];
        if (p.pricePaid != null) {
          const currencySymbol =
            p.currencyId === CurrencyType.BGN ? "лв." : "€";
          parts.push(`${p.pricePaid} ${currencySymbol}`);
        }
        if (p.paymentType) parts.push(p.paymentType);
        if (parts.length) statusText += ` — ${parts.join(", ")}`;
      }
      if (p.markedAsUnreliable) {
        statusText += " — потребителят автоматично е маркиран като нередовен";
      }
      return statusText;
    }

    case EventAuditTimelineAction.ReservationPaymentAdded: {
      const payUser = p.userName ?? "потребител";
      const payUserRef = p.userId ? ` #${p.userId}` : "";
      const payCurrencySymbol =
        p.currencyId === CurrencyType.BGN ? "лв." : "€";
      const payAmount = p.amount ? ` — ${p.amount} ${payCurrencySymbol}` : "";
      const payType = p.paymentType ? ` (${p.paymentType})` : "";
      return `${actor} добави плащане за ${payUser}${payUserRef}${payAmount}${payType}`;
    }

    case EventAuditTimelineAction.ReservationPaymentRemoved:
      return `${actor} премахна плащането на ${p.userName ?? "потребител"}${p.paymentType ? ` (${p.paymentType})` : ""}`;

    case EventAuditTimelineAction.ReservationFormCompletionToggled: {
      const formUser = p.userName ?? "потребител";
      const formUserRef = p.userId ? ` #${p.userId}` : "";
      const resRef = p.reservationId ? ` (резервация #${p.reservationId})` : "";
      return p.hasFilledForm
        ? `${actor} отбеляза попълнена форма за ${formUser}${formUserRef}${resRef}`
        : `${actor} премахна отбелязването за форма на ${formUser}${formUserRef}${resRef}`;
    }

    case EventAuditTimelineAction.ReservationCancelled: {
      const cancelUser = p.userName ?? "потребител";
      const cancelUserRef = p.userId ? ` #${p.userId}` : "";
      const cancelResRef = p.reservationId
        ? ` (резервация #${p.reservationId})`
        : "";
      return `${cancelUser}${cancelUserRef} отказа своята резервация${cancelResRef}`;
    }

    case EventAuditTimelineAction.WaitingListSpotConfirmed: {
      const wlUser = p.userName ?? "потребител";
      const wlResRef = p.reservationId ? ` (резервация #${p.reservationId})` : "";
      const wlStatus = p.newStatus === "Confirmed" ? "Потвърден" : p.newStatus === "Pending" ? "Изчакване на плащане" : p.newStatus ?? "";
      const wlStripe = p.stripePaymentPending ? " — предстои Stripe плащане" : "";
      return `${wlUser} потвърди мястото си от чакащата листа${wlResRef} — статус: ${wlStatus}${wlStripe}`;
    }

    case EventAuditTimelineAction.StripePaymentSessionCreated: {
      const sessionUser = p.userName ?? "потребител";
      const sessionAmount = p.amount != null ? ` — ${p.amount} ${(p.currency ?? "eur").toUpperCase()}` : "";
      const sessionResRef = p.reservationId ? ` (резервация #${p.reservationId})` : "";
      return `Създадена Stripe сесия за плащане [${sessionUser}]${sessionResRef}${sessionAmount}`;
    }

    case EventAuditTimelineAction.StripePaymentCompleted: {
      const paidUser = escapeHtml(p.userName ?? "потребител");
      const paidAmount = p.amount != null ? ` — ${p.amount} ${escapeHtml((p.currency ?? "eur").toUpperCase())}` : "";
      const paidResRef = p.reservationId ? ` (резервация #${p.reservationId})` : "";
      const stripeId = p.stripePaymentIntentId ?? p.stripeSessionId;
      const acct = p.stripeConnectedAccountId ? `connect/accounts/${escapeHtml(p.stripeConnectedAccountId)}/` : "";
      const stripeLink = stripeId
        ? ` <a href="https://dashboard.stripe.com/${acct}payments/${escapeHtml(stripeId)}" target="_blank" rel="noopener noreferrer" style="color:#6366f1">Виж в Stripe ↗</a>`
        : "";
      return `Получено Stripe плащане [${paidUser}]${paidResRef}${paidAmount}${stripeLink}`;
    }

    case EventAuditTimelineAction.MusicSuggestionAdded:
      return `${actor} предложи „${p.trackName ?? p.trackId ?? ""}"${p.artist ? ` от ${p.artist}` : ""}`;

    case EventAuditTimelineAction.MusicSuggestionDeleted:
      return `${actor} изтри предложението „${p.trackName ?? p.trackId ?? ""}"${p.artist ? ` от ${p.artist}` : ""}`;

    case EventAuditTimelineAction.MusicSuggestionUpvoted:
      return `${actor} гласува за „${p.trackName ?? p.trackId ?? ""}"${p.artist ? ` от ${p.artist}` : ""}`;

    case EventAuditTimelineAction.MusicSuggestionUpvoteRemoved:
      return `${actor} оттегли гласа за „${p.trackName ?? p.trackId ?? ""}"${p.artist ? ` от ${p.artist}` : ""}`;

    case EventAuditTimelineAction.EventNoteAdded:
      return `${actor} добави бележка към събитието${p.noteText ? `: „${p.noteText}"` : ""}`;

    case EventAuditTimelineAction.EventNoteEdited:
      return `${actor} редактира бележка на събитието${p.noteText ? `: „${p.noteText}"` : ""}`;

    case EventAuditTimelineAction.EventNoteDeleted:
      return `${actor} изтри бележка на събитието${p.noteText ? `: „${p.noteText}"` : ""}`;

    case EventAuditTimelineAction.ReservationNoteAdded: {
      const noteUser = p.userName ?? "потребител";
      const noteUserRef = p.userId ? ` #${p.userId}` : "";
      const noteResRef = p.reservationId
        ? ` (резервация #${p.reservationId})`
        : "";
      return `${actor} добави бележка към резервацията на ${noteUser}${noteUserRef}${noteResRef}${p.noteText ? `: „${p.noteText}"` : ""}`;
    }

    case EventAuditTimelineAction.ReservationNoteEdited: {
      const editNoteUser = p.userName ?? "потребител";
      const editNoteUserRef = p.userId ? ` #${p.userId}` : "";
      const editNoteResRef = p.reservationId
        ? ` (резервация #${p.reservationId})`
        : "";
      return `${actor} редактира бележка на ${editNoteUser}${editNoteUserRef}${editNoteResRef}${p.noteText ? `: „${p.noteText}"` : ""}`;
    }

    case EventAuditTimelineAction.ReservationNoteDeleted: {
      const delNoteUser = p.userName ?? "потребител";
      const delNoteUserRef = p.userId ? ` #${p.userId}` : "";
      const delNoteResRef = p.reservationId
        ? ` (резервация #${p.reservationId})`
        : "";
      return `${actor} изтри бележка на ${delNoteUser}${delNoteUserRef}${delNoteResRef}${p.noteText ? `: „${p.noteText}"` : ""}`;
    }

    case EventAuditTimelineAction.WorkoutPlanEdited:
      return `${actor} направи промени по тренировъчната програма`;

    case EventAuditTimelineAction.WorkoutPlanDeleted:
      return `${actor} изтри тренировъчната програма`;

    default:
      return "Неизвестно действие";
  }
}

export type EventAuditCategory =
  | "lifecycle"
  | "reservation"
  | "status"
  | "music"
  | "notes";

export function getEventAuditActionCategory(
  action: number,
): EventAuditCategory {
  const a = action as EventAuditTimelineAction;

  if (
    [
      EventAuditTimelineAction.EventCreated,
      EventAuditTimelineAction.EventEdited,
      EventAuditTimelineAction.EventBulkEdited,
      EventAuditTimelineAction.EventDeleted,
      EventAuditTimelineAction.EventRestored,
      EventAuditTimelineAction.EventHardDeleted,
      EventAuditTimelineAction.EventDuplicated,
      EventAuditTimelineAction.PortalAppointmentBooked,
      EventAuditTimelineAction.InstructorBulkAssigned,
    ].includes(a)
  )
    return "lifecycle";

  if (
    [
      EventAuditTimelineAction.ReservationAdded,
      EventAuditTimelineAction.ReservationDeleted,
      EventAuditTimelineAction.ReservationPermanentlyDeleted,
      EventAuditTimelineAction.ReservationRestored,
      EventAuditTimelineAction.ReservationCancelled,
      EventAuditTimelineAction.WaitingListSpotConfirmed,
      EventAuditTimelineAction.ReservationPaymentAdded,
      EventAuditTimelineAction.ReservationPaymentRemoved,
      EventAuditTimelineAction.ReservationFormCompletionToggled,
      EventAuditTimelineAction.StripePaymentSessionCreated,
      EventAuditTimelineAction.StripePaymentCompleted,
    ].includes(a)
  )
    return "reservation";

  if ([EventAuditTimelineAction.ReservationStatusChanged].includes(a))
    return "status";

  if (
    [
      EventAuditTimelineAction.MusicSuggestionAdded,
      EventAuditTimelineAction.MusicSuggestionDeleted,
      EventAuditTimelineAction.MusicSuggestionUpvoted,
      EventAuditTimelineAction.MusicSuggestionUpvoteRemoved,
    ].includes(a)
  )
    return "music";

  return "notes";
}

export const EVENT_AUDIT_CATEGORY_COLORS: Record<EventAuditCategory, string> = {
  lifecycle: "#6366f1",
  reservation: "#10b981",
  status: "#f59e0b",
  music: "#ec4899",
  notes: "#3b82f6",
};

const dot = (color: string) =>
  `<span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${color};margin-right:8px;vertical-align:middle;flex-shrink:0"></span>`;

export const EVENT_AUDIT_COLOR_LEGEND_HTML = `
<div style="line-height:2;min-width:180px">
  <div>${dot("#6366f1")}Събитие</div>
  <div>${dot("#10b981")}Резервации</div>
  <div>${dot("#f59e0b")}Статуси</div>
  <div>${dot("#ec4899")}Музика</div>
  <div>${dot("#3b82f6")}Бележки</div>
</div>`.trim();

export const EVENT_HISTORY_CONFIG: IHistoryConfig = {
  historyEndpoint: endpointsMetadata.eventAuditLog.getEventHistory,
  historyQueryParam: "eventId",
  detailEndpoint: endpointsMetadata.eventAuditLog.getDetail,
  cutoffDate: EVENT_HISTORY_CUTOFF_DATE,
  cutoffDateLabel: "04.04.2026 г.",
  tooltipHtml: EVENT_AUDIT_COLOR_LEGEND_HTML,
  generateText: generateEventAuditText,
  getCategory: getEventAuditActionCategory,
  categoryColors: EVENT_AUDIT_CATEGORY_COLORS,
};
